import { useState, useEffect } from "react";
// @mui
import {
  Dialog,
  DialogContent,
  Button,
  Stack,
  Typography,
  IconButton,
  Box,
  DialogActions,
} from "@mui/material";
// hooks
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
// components
import Iconify from "../../components/Iconify";
import SvgIconStyle from "../../components/SvgIconStyle";
import DIOText from "../../components/DIOText";
import Label from "../../components/Label";
// utils
import axios from "../../utils/axios";

// ----------------------------------------------------------------------

export default function FreeToProDialog({ open, closeDialog, buttonSource="page" }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const [requested, setRequested] = useState(false);

  const planGuide = buttonSource === "page";
  const tryCoffeePage = buttonSource === "try-coffee";
  const canCoffeeChat = buttonSource === "coffee-chat";
  const canMessage = buttonSource === "message";
  const requestProfile = buttonSource === "request-profile";

  useEffect(() => {
    if (open) {
      setRequested(false);
    }
  }, [open]);

  const requestUpgrade = () => {
    try {
      const companyId = user?.employee.company?.[0]?.id;
      axios.post(`/api/company/${companyId}/scout/apply-pro/`, {source: planGuide ? "pro" : buttonSource});
    } catch (err) {
      console.error(err);
    }
    setRequested(true);
  };

  const formatPhone = (phone) => {
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
      return match[1] + '-' + match[2] + '-' + match[3];
    }
    return phone;
  };

  return (
    <Dialog open={open} onClose={closeDialog} fullScreen={!smUp && !requested} fullWidth maxWidth={requested ? "dg" : "dp"}>
      {!requested && (
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack alignItems="flex-end">
          <IconButton edge="end" onClick={closeDialog} color="primary">
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
        {planGuide && (
        <Typography
          variant={"h3"}
          gutterBottom
          sx={{ textAlign: smUp ? "center" : "inherit" }}
        >
          더 많은 인재와 만나보세요.
        </Typography>
        )}
        {planGuide && (
        <Typography variant={smUp ? "body1" : "body2"} sx={{ letterSpacing: smUp ? 0 : -0.26, textAlign: smUp ? "center" : "inherit", mb: { xs: 2, sm: 4 } }}>
          <DIOText />의 검증된 경력자 인재 풀을 인재 영입에 활용해 보세요.
        </Typography>
        )}
        {tryCoffeePage && (
          <Typography
            variant={"h3"}
            gutterBottom
            sx={{ textAlign: smUp ? "center" : "inherit", mb: { xs: 2, sm: 4 } }}
          >
            플랜을 업그레이드하고,<br/>
            크루와 직접 컨택하세요!
          </Typography>
        )}
        {canCoffeeChat && (
          <Typography
            variant={"h3"}
            gutterBottom
            sx={{ textAlign: smUp ? "center" : "inherit", mb: { xs: 2, sm: 4 } }}
          >
            플랜을 업그레이드하면 <br/>
            크루와 커피챗 할 수 있어요!
          </Typography>
        )}
        {canMessage && (
          <Typography
            variant={"h3"}
            gutterBottom
            sx={{ textAlign: smUp ? "center" : "inherit", mb: { xs: 2, sm: 4 } }}
          >
            플랜을 업그레이드하면 <br/>
            크루에게 메시지를 보낼 수 있어요!
          </Typography>
        )}
        {requestProfile && (
          <Typography
            variant={"h3"}
            gutterBottom
            sx={{ textAlign: smUp ? "center" : "inherit", mb: { xs: 2, sm: 4 } }}
          >
            플랜을 업그레이드하면 <br/>
            크루에게 프로필을 더 요청할 수 있어요!
          </Typography>
        )}
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={3}>
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              bgcolor: "grey.100",
              width: 1,
            }}
          >
            <Label variant="ghost" color="default">
              무료 플랜
            </Label>
            <Typography variant="h3" sx={{ mt: 1, mb: 3 }}>
              0원
            </Typography>
            <Stack spacing={1} sx={{ mb: 5 }}>
              <Stack direction="row" alignItems="start" spacing={1}>
                <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "text.disabled", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="body2">
                  총 3명의 경력자 인재 추천
                </Typography>
              </Stack>
              {smUp && (
              <Stack direction="row" alignItems="start" spacing={1} sx={{ visibility: "hidden" }}>
                <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "text.disabled", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="body2">
                  -
                </Typography>
              </Stack>
              )}
              {smUp && (
              <Stack direction="row" alignItems="start" spacing={1} sx={{ visibility: "hidden" }}>
                <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "text.disabled", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="body2">
                  -
                </Typography>
              </Stack>
              )}
            </Stack>
            <Button variant="outlined" size="large" fullWidth>
              현재 플랜
            </Button>
          </Box>
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              border: "1px solid black",
              width: 1,
            }}
          >
            <Label variant="filled" color="primary">
              PRO 플랜
            </Label>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 3 }}>
              <Typography variant="h3">
                49,000원
              </Typography>
              <Typography variant="h4" color="text.secondary">
                /월
              </Typography>
            </Stack>
            <Stack spacing={1} sx={{ mb: 5 }}>
              <Stack direction="row" alignItems="start" spacing={1}>
                <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "text.primary", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="body2">
                  매월 검증된 경력자 20명 컨택 포인트 제공
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="start" spacing={1}>
                <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "text.primary", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="body2">
                  실시간 피드백이 적용되는 맞춤 인재 매칭
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="start" spacing={1}>
                <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "text.primary", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="body2">
                  1:1 전담 인재 서치 컨설팅 제공
                </Typography>
              </Stack>
            </Stack>
            <Button variant="contained" size="large" fullWidth onClick={requestUpgrade}>
              업그레이드
            </Button>
            <Typography sx={{ mt: 1.5, color: "text.secondary", textAlign: "center", fontSize: (theme) => theme.typography.caption.fontSize }}>
              연단위 구독 필요 (VAT 별도)
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      )}
      {requested && (
        <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
          <Typography variant={smUp ? "h5" : "subtitle1"} paragraph>
            플랜 업그레이드 안내를 위해{!smUp && <br />} <DIOText />에서 연락드릴게요.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
            • 연락 받으실 전화번호 : <b>{formatPhone(user?.phone)}</b>
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
            • 영업일 3일 이내에 연락드리겠습니다.
          </Typography>
        </DialogContent>
      )}
      {requested && (
        <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
          <Button onClick={closeDialog} color="primary" variant="contained" fullWidth={!smUp} size="large" sx={{ px: 6 }}>
            확인했어요
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}